import { useNavigate } from 'react-router-dom';
import TableForm from 'components/common/lists/TableForm';
import Paging from 'components/common/nav/Paging';
import CheckPatientComplaint from 'pages/modal/CheckPatientComplaint';
import styles from 'styles/components/common/lists/Table.module.scss';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron_right.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { TStudentChartTableInfo } from 'types/apis/professor';
import { TTableColumn, TTablePaging } from 'types/components/table';
import useModal from 'hooks/useModal';

type TProps = {
  courseInfoId: number | undefined;
  chartRound: number | undefined;
  studentUserId: number;
  tableData: TStudentChartTableInfo[];
  tablePaging: TTablePaging | undefined;
  page: number;
  setPage: (page: number) => void;
  sortBy: string;
  sortDirection: string;
  onClickSort: (sortBy: string) => void;
  isLoading: boolean;
};

const tableColumns: TTableColumn[] = [
  { name: 'no', label: '번호', width: '80px' },
  { name: 'chartTypeName', label: '차트 종류', width: '240px' },
  { name: '', label: '주요 호소 증상', width: '180px' },
  {
    name: 'lastEvaluatedAt',
    label: '평가일시',
    width: '160px',
    onClick: () => {
      return;
    },
  },
  {
    name: 'score',
    label: '평가결과',
    width: '180px',
    onClick: () => {
      return;
    },
  },
  { name: '', label: '차트 상세보기', width: '180px' },
];

const StudentChartList = ({
  courseInfoId,
  chartRound,
  studentUserId,
  tableData,
  tablePaging,
  page,
  setPage,
  sortBy,
  sortDirection,
  onClickSort,
  isLoading,
}: TProps) => {
  const navigate = useNavigate();

  const { openModal } = useModal();

  const openCheckComplaint = (text: string) =>
    openModal({
      element: <CheckPatientComplaint text={text} />,
    });

  const goToChart = (
    id: number,
    chartType: number,
    evaluationRound: number
  ) => {
    const chartRoute = ChartInfo.find((chart) => chart.type === chartType)
      ?.route;
    if (!chartRoute || !courseInfoId || !chartRound || !studentUserId) {
      openModal({
        title: '차트 조회 실패',
        text: '관리자에게 문의해 주세요.',
      });
      return;
    }
    sessionStorage.setItem('route', 'student');
    sessionStorage.setItem('page', String(page));
    navigate(
      `/feedback${chartRoute}/${id}?courseInfoId=${courseInfoId}&chartRound=${chartRound}&studentUserId=${studentUserId}&tab=${
        evaluationRound === 2 ? 2 : 0
      }`
    );
  };

  const renderActions = (row: TStudentChartTableInfo, headerIndex?: number) => (
    <>
      {headerIndex === 2 && (
        <>
          {row.complaint ? (
            <button
              type="button"
              className={styles.link_s}
              onClick={() => openCheckComplaint(row.complaint)}
            >
              <span>
                <span className={styles.ellipsis}>{row.patientName}</span>{' '}
                상세보기
              </span>
              <ChevronRightIcon
                width={20}
                height={20}
                fill={STYLES_VARIABLES.PURPLE_01}
              />
            </button>
          ) : (
            '-'
          )}
        </>
      )}
      {headerIndex === 5 && (
        <button
          type="button"
          className={styles.link_s}
          onClick={() =>
            goToChart(row.evaluationChartId, row.chartType, row.evaluationRound)
          }
          disabled={!row.evaluationChartId}
        >
          차트 상세보기
          <ChevronRightIcon
            width={20}
            height={20}
            fill={
              STYLES_VARIABLES[!row.evaluationChartId ? 'GRAY_05' : 'PURPLE_01']
            }
          />
        </button>
      )}
    </>
  );

  return (
    <div className="table_container">
      <div className="flex items_center justify_between mt32 mb24">
        <div className="flex items_center">
          <h1 className="fs24 fw700 mr24">등록된 차트</h1>
          <span
            style={{ color: STYLES_VARIABLES.PURPLE_01 }}
            className="fs20 fw700"
          >
            총 {tablePaging?.totalElements || 0}개
          </span>
        </div>
      </div>

      <TableForm
        data={tableData}
        columns={tableColumns}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onClickSort={onClickSort}
        renderActions={renderActions}
        emptyMessage="등록된 차트가 없습니다."
        isLoading={isLoading}
      />

      {tablePaging && tablePaging.totalElements > 0 && (
        <div className="mt32">
          <Paging
            activePage={page}
            totalItemsCount={tablePaging.totalElements}
            handlePageChange={setPage}
          />
        </div>
      )}
    </div>
  );
};

export default StudentChartList;
